import {
  INSET_MAX,
  INSET_MIN,
  INSET_PRESET_VALUES,
  INSET_RANGE_ERROR,
  isInsetEligibleShape,
  normInset,
} from '@/lib/object-effects/sides'
import { buildInsetHistoryItem, buildInsetLiveUpdate } from '../helpers/polygon-effect-updates'
import { useGeometryEffectControl } from '../hooks/use-geometry-effect-control'
import type { EffectControlProps } from '../types/effect-control-props'
import type { GeometryEffect } from '../types/geometry-effect'
import { EffectSliderField } from './effect-slider-field'

const INSET_PRESETS = INSET_PRESET_VALUES.map((v) => ({ label: `${v}°`, value: v }))

function parseInsetInput(raw: string): number | null {
  const t = raw
    .trim()
    .replace(/°+\s*$/u, '')
    .trim()

  if (t === '') return null
  if (!/^-?\d+$/u.test(t)) return null
  const n = Number.parseInt(t, 10)

  return Number.isFinite(n) ? n : null
}

function parseInsetFieldInput(raw: string): number | null {
  const parsed = parseInsetInput(raw)

  if (parsed == null || parsed < INSET_MIN || parsed > INSET_MAX) return null
  return parsed
}

const INSET_EFFECT: GeometryEffect = {
  restValue: INSET_MIN,
  min: INSET_MIN,
  max: INSET_MAX,
  presets: INSET_PRESETS,
  rangeError: INSET_RANGE_ERROR,
  isEligible: isInsetEligibleShape,
  readValue: normInset,
  normalize: (value) => Math.max(INSET_MIN, Math.min(INSET_MAX, Math.round(value))),
  parseInput: parseInsetFieldInput,
  liveUpdate: buildInsetLiveUpdate,
  historyItem: buildInsetHistoryItem,
  startSource: 'live',
}

/**
 * Inset 0–100° for eligible polygon / star shapes (and rectangles Sides has converted). The field
 * behavior and multi-selection rules are shared with Sides / Rounding via
 * `use-geometry-effect-control`.
 */
export function EffectsPanelInset(props: EffectControlProps) {
  const field = useGeometryEffectControl(INSET_EFFECT, props)

  return (
    <EffectSliderField
      {...field}
      label="Inset"
      ariaLabel="Inset value"
      errorId="inset-input-error"
      suffix="°"
    />
  )
}
